<template>
  <v-app>
    <app-top-bar :class="hasBackgroundImage ? '' : 'bg-background'" :path-return="pathPage">{{ $t("emailVerification.pageTitle") }}</app-top-bar>

    <v-main :style="mainStyle" :class="hasBackgroundImage ? '' : 'bg-background'">
      <v-container v-if="!showComplete">
        <v-alert
          v-if="alertStore.isVisible"
          type="error"
          icon="$error"
          class="mb-6 text-center"
        >
          {{ alertStore.getText }}
        </v-alert>

        <h1 class="page-title mb-6 text-on-surface-variant">
          {{ $t("emailVerification.mainTitle") }}
        </h1>
        <p class="mb-6 text-on-surface-variant">
          {{ $t("emailVerification.description", { phone: redactedPhone }) }}
        </p>

        <div class="text-center">
          <v-text-field
            v-model="code"
            v-mask="'######'"
            class="mb-1 text-on-surface-variant"
            variant="underlined"
            :label="$t('emailVerification.codeLabel')"
            maxlength="6"
            type="tel"
            color="primary"
            background-color="surface"
          />

          <app-button
            class="text-center"
            color="primary"
            variant="text"
            x-large
            @click="onResendConfirmationCode"
          >
            <v-icon class="mr-2" :icon="mdiRefresh" />
            {{ $t("emailVerification.resendButton") }}
          </app-button>
        </div>
      </v-container>
      <v-container v-if="showComplete">
        <phase-completed />
      </v-container>
    </v-main>

    <app-footer>
      <continue-footer-button
        v-if="!showComplete"
        color="primary"
        :disabled="!isCodeInputValid || isChecking"
        :loading="isAdvancing || isChecking"
        @click="advance"
      />
      <continue-footer-button v-if="showComplete" :loading="isAdvancing" @click="advanceFlow" />
    </app-footer>
  </v-app>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { mask as vMask } from "vue-the-mask";

import { usePageBackground } from "@/composables/usePageBackground";
import { useStepFlow } from "@/composables/useStepFlow";
import { useUtils } from "@/composables/useUtils";
import { useAlertStore } from "@/stores/alert";
import { useApiStore } from "@/stores/api";
import { useAppStore } from "@/stores/app";
import { showToast } from "@/utils/toast.ts";
import { mdiRefresh } from "@mdi/js";

const api = useApiStore();
const app = useAppStore();
const alertStore = useAlertStore();
const { mainStyle, hasBackgroundImage } = usePageBackground();
const util = useUtils();
const pathPage = '/client-verification';

const { isAdvancing, showComplete, advance: advanceFlow } = useStepFlow(pathPage, () => isComplete.value);
const isChecking = ref(false);
const code = ref(null);
const isCodeInputValid = computed(() => code.value && code.value.length === 6);
const isComplete = computed<boolean>(() => api.isItemComplete("verification"));

const redactedPhone = computed(() => {
  const phone = api.client?.phone;
  return phone ? phone.replace(/(\+\d{2}\s\(\d{2}\)\s)\d{5}/, "$1*****") : null;
});

onMounted(() => {
  if (alertStore.isVisible && api.client?.email) {
    onResendConfirmationCode();
  }
});

async function onResendConfirmationCode() {
  const result: any = await api.sendUserValidation(api.client.email);
  if (result && result.msg == 'OK') {
    showToast.success({
      title: 'SMS enviado com sucesso',
      description: ''
    });
  }
}

async function advance() {
  if (isChecking.value || isAdvancing.value) {
    return;
  }
  isChecking.value = true;
  try {
    if (api.insuranceCode) {
      await api.confirmClientForJourney(code.value!);
    } else {
      await api.verifyClientCode(code.value!);
      await api.ensureInsuranceForClient();
    }

    alertStore.dispose();
    await advanceFlow();
  } catch (error) {
    console.error(error);
    util.noticeError(error);
  } finally {
    isChecking.value = false;
  }
}
</script>

<style lang="scss" scoped>
p {
  margin-bottom: 1.25rem;
}
</style>
